import type { MetaFunction } from '@remix-run/node';
import { memo } from 'react';
import { buildMeta } from '~/utils/meta.js';

export const meta: MetaFunction = (args) => {
  return buildMeta(args, { title: 'Terms of Service | hostU' });
};

const termsOfService = memo(() => (
  <div className="mx-auto max-w-[800px] px-4 py-16">
    <div className="py-8 font-bold text-4xl">
      <p>hostU Terms of Service</p>
    </div>
    <div className="space-y-6 text-xl leading-10">
      <p>
        Please read these Terms of Service (“Terms”) carefully as they contain important information
        about your legal rights, remedies and obligations. By accessing or using the HostU Platform,
        you agree to comply with and be bound by these Terms.
      </p>
      <p> Last Updated: June 4, 2024</p>
      <p> Thank you for using HostU!</p>

      <p>
        These Terms constitute a legally binding agreement ("Agreement") between you and hostU (as
        defined below) governing your access to and use of the hostU website, including any
        subdomains thereof, and any other websites through which hostU makes its services available
        (collectively, "Site"), and any future mobile, tablet and other smart device applications,
        and application program interfaces (collectively, "Application") and all associated services
        (collectively, "hostU Services"). The Site, Application and hostU Services together are
        hereinafter collectively referred to as the “hostU Platform”. Our Nondiscrimination Policy,
        Privacy Policy, Sublet Agreement and other Policies applicable to your use of the hostU
        Platform are incorporated by reference into this Agreement. Our collection and use of
        personal information in connection with your access to and use of the hostU Platform is
        described in our Privacy Policy. When these Terms mention “hostU,” “we,” “us,” or “our,” it
        refers to hostU, Inc., a Delaware corporation.
      </p>
      <p>
        Hosts alone are responsible for identifying, understanding, and complying with their lease
        and all laws, rules and regulations that apply to their Listings. For example, some leases
        prohibit sublets without the consent or agreement of the landlord. Many cities have passed
        local laws that actually prohibit the enforcement of this provision in leases. Some local
        laws prohibit sublets for short term stays, which are typically defined as less than 30
        days. Hosts are alone responsible for identifying and obtaining any required consents,
        licenses or permits. These Terms of Service and the Sublet Agreement that you enter into
        upon completing a booking, may or may not address the terms of your lease and local laws. It
        is a general booking agreement that outlines the days of the sublet and responsibilities
        that generally apply to Hosts and Guests who complete bookings on the hostU Platform. It
        should not be construed as legal advice for either the Host or Guest, and may need to be
        supplemented by a separate agreement between the Host or Guest in order to address your
        particular situation. If you have questions about how your lease, the Sublet Agreement
        and/or local laws apply to your Listing(s) and Host Service(s) on hostU, you should always
        seek your own legal guidance as hostU is solely a platform for Members to post content and
        does not provide any legal advice or guidance.
      </p>
      <p className="font-semibold text-3xl">1. Scope of hostU Services</p>
      <p>
        1.1 The hostU Platform is an online marketplace that enables registered users (“Members”)
        (Members who publish listings are referred to as “Hosts”) to publish such the Listings on
        the hostU Platform (“Listings”) and to communicate and transact directly with Members that
        are seeking to book the Listing (Members seeking or booking Listings are referred to herein
        as “Guests”).
      </p>
      <p>
        1.2 As the provider of the hostU Platform, hostU does not own, create, sell, resell,
        provide, control, manage, offer, deliver, or supply any Listings. Hosts alone are
        responsible for their Listings. When Members make or accept a booking, they are entering
        into a contract directly with each other. hostU is not and does not become a party to or
        other participant in any contractual relationship between Members, nor is hostU a real
        estate broker or insurer. hostU is not acting as an agent in any capacity for any Member.
      </p>
      <p>
        1.3 While we may help facilitate the resolution of disputes, hostU has no control over and
        does not guarantee (i) the existence, quality, safety, suitability, or legality of any
        Listings, (ii) the truth or accuracy of any Listing descriptions, Ratings, Reviews, or other
        Member Content (as defined below), or (iii) the performance or conduct of any Member or
        third party. hostU does not endorse any Member, Listing or Listing. Any references to a
        Member being "verified" as a University student, faculty or graduate (or similar language)
        only indicates that the Member has completed a relevant verification or identification
        process and nothing else. hostU verifies university affiliation through an email
        confirmation process tied to university emails. In the event that an individual is able to
        obtain access to a university email address, gain access to another Member’s hostU account,
        or otherwise bypasses the verification process through fraudulent or unscrupulous means,
        means that they may not have the university affiliation that they claimed in their
        onboarding process. Any “verification” description by hostU of a Host or Guest is not an
        endorsement, certification or guarantee by hostU about any Member, including of the Member's
        identity or background or whether the Member is trustworthy, safe or suitable. You should
        always exercise due diligence and care when deciding whether to stay in an Accommodation,
        accept a booking request from a Guest, or communicate and interact with other Members,
        whether online or in person. Verified Images (as defined below) are intended only to
        indicate a photographic representation of a Listing at the time the photograph was taken and
        are therefore not an endorsement by hostU of any Host or Listing.
      </p>
      <p>
        1.4 If you choose to use the hostU Platform as a Host or Co-Host (as defined below), your
        relationship with hostU is limited to being an independent, third-party contractor, and not
        an employee, agent, joint venturer or partner of hostU for any reason, and you act
        exclusively on your own behalf and for your own benefit, and not on behalf, or for the
        benefit, of hostU. hostU does not, and shall not be deemed to, direct or control you
        generally or in your performance under these Terms specifically, including in connection
        with your sublet. You acknowledge and agree that you have complete discretion whether to
        publish a Listing, communicate with Guests and accept a booking.
      </p>
      <p>
        1.5 To promote the hostU Platform and to increase the exposure of Listings to potential
        Guests, Listings and other Member Content may be displayed on other websites, in
        applications, within emails, and in online and offline advertisements. To assist Members who
        speak different languages, Listings and other Member Content may be translated, in whole or
        in part, into other languages. hostU cannot guarantee the accuracy or quality of such
        translations and Members are responsible for reviewing and verifying the accuracy of such
        translations. The hostU Platform may contain translations powered by Google or other
        translation providers. hostU disclaims all warranties related to the translations, express
        or implied, including any warranties of accuracy, reliability, and any implied warranties
        for merchantability, fitness for a particular purpose and non-infringement.
      </p>
      <p>
        1.6 The hostU Platform may contain links to third-party websites or resources (“Third-Party
        Services”). Such Third-Party Services may be subject to different terms and conditions and
        privacy practices. hostU is not responsible or liable for the availability or accuracy of
        such Third-Party Services, or the content, products, or services available from such
        Third-Party Services. Links to such Third-Party Services are not an endorsement by hostU of
        such Third-Party Services.
      </p>
      <p>
        1.7 Due to the nature of the Internet, hostU cannot guarantee the continuous and
        uninterrupted availability and accessibility of the hostU Platform. hostU may restrict the
        availability of the hostU Platform or certain areas or features thereof, if this is
        necessary in view of capacity limits, the security or integrity of our servers, or to carry
        out maintenance measures that ensure the proper or improved functioning of the hostU
        Platform. hostU may improve, enhance and modify the hostU Platform and introduce new hostU
        Services from time to time.
      </p>
      <p className="font-semibold text-3xl">
        2. Eligibility, Using the hostU Platform, Member Verification
      </p>
      <p>
        2.1 In order to access and use the hostU Platform or register an hostU Account you must be
        an individual at least 18 years old or a duly organized, validly existing business,
        organization or other legal entity in good standing under the laws of the country you are
        established and able to enter into legally binding contracts.
      </p>
      <p>
        2.2 hostU may make access to and use of the hostU Platform, or certain areas or features of
        the hostU Platform, subject to certain conditions or requirements, such as completing a
        verification process, meeting specific quality or eligibility criteria, meeting Ratings or
        Reviews thresholds, or a Member’s booking and cancellation history.
      </p>
      <p>
        2.3 User verification is difficult, and we do not assume any responsibility for the
        confirmation of any Member’s identity including without limitation an individual’s
        affiliation with a University. Notwithstanding the above, for transparency and fraud
        prevention purposes, and as permitted by applicable laws, we may, but have no obligation to
        (i) ask Members to provide a form of University identification, government identification or
        other information or undertake additional checks designed to help verify the identities or
        backgrounds of Members, (ii) screen Members against third party databases or other sources
        and request reports from service providers, and (iii) where we have sufficient information
        to identify a Member, obtain reports from public records of criminal convictions or sex
        offender registrations or an equivalent version of background or registered sex offender
        checks in your local jurisdiction (if available).
      </p>
      <p>
        2.4 The access to or use of certain areas and features of the hostU Platform may be subject
        to separate policies, standards or guidelines, or may require that you accept additional
        terms and conditions, before you can access the relevant areas or features of the hostU
        Platform. If there is a conflict between these Terms and terms and conditions applicable to
        a specific area or feature of the hostU Platform, the latter terms and conditions will take
        precedence with respect to your access to or use of that area or feature, unless specified
        otherwise in the latter terms and conditions.
      </p>
      <p className="font-semibold text-3xl">3. Modification of these Terms</p>
      <p>
        hostU reserves the right to modify these Terms at any time in accordance with this
        provision. If we make changes to these Terms, we will post the revised Terms on the hostU
        Platform and update the “Last Updated” date at the top of these Terms. If you disagree with
        the revised Terms, you may terminate this Agreement with immediate effect. If you do not
        terminate your Agreement before the date the revised Terms become effective, your continued
        access to or use of the hostU Platform will constitute acceptance of the revised Terms.
      </p>
      <p className="font-semibold text-3xl">4. Account Registration</p>
      <p>
        4.1 You must register an account ("hostU Account") to access and use certain features of the
        hostU Platform, such as publishing or booking a Listing. If you are registering a hostU
        Account for a business, organization or other legal entity, you represent and warrant that
        you have the authority to legally bind that entity and grant us all permissions and licenses
        provided in these Terms.
      </p>
      <p>
        4.2 You can register a hostU Account using a university email address and creating a
        password.
      </p>
      <p>
        4.3 You must provide accurate, current and complete information during the registration
        process and keep your hostU Account and public hostU Account profile page information up to
        date at all times.
      </p>
      <p>
        4.4 You may not register more than one (1) hostU Account unless hostU authorizes you to do
        so. You may not assign or otherwise transfer your hostU Account to another party.
      </p>
      <p>
        4.5 You are responsible for maintaining the confidentiality and security of your hostU
        Account credentials and may not disclose your credentials to any third party. You must
        immediately notify hostU if you know or have any reason to suspect that your credentials
        have been lost, stolen, misappropriated, or otherwise compromised or in case of any actual
        or suspected unauthorized use of your hostU Account. You are liable for any and all
        activities conducted through your hostU Account, unless such activities are not authorized
        by you, and you are not otherwise negligent (such as failing to report the unauthorized use
        or loss of your credentials).
      </p>
      <p>
        4.6 hostU may enable features that allow you to authorize other Members or certain third
        parties to take certain actions that affect your hostU Account. For example, we may enable
        Members to link their hostU Accounts to businesses and take actions for those businesses, we
        may enable eligible Members or certain third parties to book Listings on behalf of other
        Members, or we may enable Hosts to add other Members as Co-Hosts (as defined below) to help
        manage their Listings. These features do not require that you share your credentials with
        any other person. No third party is authorized by hostU to ask for your credentials, and you
        shall not request the credentials of another Member.
      </p>
      <p className="font-semibold text-3xl">5. Content</p>
      <p>
        5.1 hostU may, at its sole discretion, enable Members to (i) communicate with each other,
        create, upload, post, send, receive and store content, such as text, photos, audio, video,
        or other materials and information on or through the hostU Platform ("Member Content"); and
        (ii) access and view Member Content and any content that hostU itself makes available on or
        through the hostU Platform, including proprietary hostU content and any content licensed or
        authorized for use by or through hostU from a third party ("hostU Content" and together with
        Member Content, "Collective Content").
      </p>
      <p>
        5.2 The hostU Platform, hostU Content, and Member Content may in its entirety or in part be
        protected by copyright, trademark, and/or other laws of the United States and other
        countries. You acknowledge and agree that the hostU Platform and hostU Content, including
        all associated intellectual property rights, are the exclusive property of hostU and/or its
        licensors or authorizing third parties. You will not remove, alter or obscure any copyright,
        trademark, service mark or other proprietary rights notices incorporated in or accompanying
        the hostU Platform, hostU Content or Member Content. All trademarks, service marks, logos,
        trade names, and any other source identifiers of hostU used on or in connection with the
        hostU Platform and hostU Content are trademarks or registered trademarks of hostU in the
        United States and abroad. Trademarks, service marks, logos, trade names and any other
        proprietary designations of third parties used on or in connection with the hostU Platform,
        hostU Content, and/or Collective Content are used for identification purposes only and may
        be the property of their respective owners.
      </p>
      <p>
        5.3 You will not use, copy, adapt, modify, prepare derivative works of, distribute, license,
        sell, transfer, publicly display, publicly perform, transmit, broadcast or otherwise exploit
        the hostU Platform or Collective Content, except to the extent you are the legal owner of
        certain Member Content or as expressly permitted in these Terms. No licenses or rights are
        granted to you by implication or otherwise under any intellectual property rights owned or
        controlled by hostU or its licensors, except for the licenses and rights expressly granted
        in these Terms.
      </p>
      <p>
        5.4 Subject to your compliance with these Terms, hostU grants you a limited, non-exclusive,
        non-sublicensable, revocable, non-transferable license to (i) download and use the
        Application on your personal device(s); and (ii) access and view any Collective Content made
        available on or through the hostU Platform and accessible to you, solely for your personal
        and non-commercial use.
      </p>
      <p>
        5.5 By creating, uploading, posting, sending, receiving, storing, or otherwise making
        available any Member Content on or through the hostU Platform, you grant to hostU a
        non-exclusive, worldwide, royalty-free, irrevocable, perpetual (or for the term of the
        protection), sub-licensable and transferable license to such Member Content to access, use,
        store, copy, modify, prepare derivative works of, distribute, publish, transmit, stream,
        broadcast, and otherwise exploit in any manner such Member Content to provide and/or promote
        the hostU Platform, in any media or platform. Insofar as Member Content (including Verified
        Images) includes personal information, such Member Content will only be used for these
        purposes if such use complies with applicable data protection laws in accordance with our
        Privacy Policy. Unless you provide specific consent, hostU does not claim any ownership
        rights in any Member Content and nothing in these Terms will be deemed to restrict any
        rights that you may have to use or exploit your Member Content.
      </p>
      <p>
        5.6 hostU may offer Hosts the option of having professional photographers take photographs
        of their Listing, which are made available by the photographer to Hosts to include in their
        Listings with or without a watermark or tag bearing the words "hostU.com Verified Photo" or
        similar wording ("Verified Images"). You are responsible for ensuring that your Host Service
        is accurately represented in the Verified Images and you will stop using the Verified Images
        on or through the hostU Platform if they no longer accurately represent your Listing, if you
        stop hosting the Host Service featured, or if your hostU Account is terminated or suspended
        for any reason. You acknowledge and agree that hostU shall have the right to use any
        Verified Images in accordance with Section 5.5 for advertising, marketing and/or any other
        business purposes in any media or platform, whether in relation to your Listing or
        otherwise, without further notice or compensation to you. Where hostU is not the exclusive
        owner of Verified Images, by using such Verified Images on or through the hostU Platform,
        you grant to hostU an exclusive, worldwide, royalty-free, irrevocable, perpetual (or for the
        term of the protection), sub-licensable and transferable license to use such Verified Images
        for advertising, marketing and/or any other business purposes in any media or platform,
        whether in relation to your Listing or otherwise, without further notice or compensation to
        you. hostU in turn grants you a limited, non-exclusive, non-sublicensable, revocable,
        non-transferable license to use Verified Images outside of the hostU Platform solely for
        your personal and non-commercial use.
      </p>
      <p>
        5.7 You are solely responsible for all Member Content that you make available on or through
        the hostU Platform. Accordingly, you represent and warrant that: (i) you either are the sole
        and exclusive owner of all Member Content that you make available on or through the hostU
        Platform or you have all rights, licenses, consents and releases that are necessary to grant
        to hostU the rights in and to such Member Content, as contemplated under these Terms; and
        (ii) neither the Member Content nor your posting, uploading, publication, submission or
        transmittal of the Member Content or hostU's use of the Member Content (or any portion
        thereof) as contemplated under these Terms will infringe, misappropriate or violate a third
        party's patent, copyright, trademark, trade secret, moral rights or other proprietary or
        intellectual property rights, or rights of publicity or privacy, or result in the violation
        of any applicable law or regulation.
      </p>
      <p>
        5.8 You will not post, upload, publish, submit or transmit any Member Content that: (i) is
        fraudulent, false, misleading (directly or by omission or failure to update information) or
        deceptive; (ii) is defamatory, libelous, obscene, pornographic, vulgar or offensive; (iii)
        promotes discrimination, bigotry, racism, hatred, harassment or harm against any individual
        or group; (iv) is violent or threatening or promotes violence or actions that are
        threatening to any other person or animal; (v) promotes illegal or harmful activities or
        substances; or (vi) violates hostU’s Content Policy or any other hostU policy. hostU may,
        without prior notice, remove or disable access to any Member Content that hostU finds to be
        in violation of applicable law, these Terms or hostU’s then-current Policies or Standards,
        or otherwise may be harmful or objectionable to hostU, its Members, third parties, or
        property.
      </p>
      <p>
        5.9 hostU respects copyright law and expects its Members to do the same. If you believe that
        any content on the hostU Platform infringes copyrights you own, please notify us in
        accordance with our Copyright Policy.
      </p>
      <p className="font-semibold text-3xl">6. Service Fees</p>
      <p>
        6.1 hostU charges a 6% transaction fee for any transaction that was originated on the hostU
        Platform, whether the transaction was completed on the hostU Platform or not (the “hostU
        Fee”) and may charge additional fees to Hosts ("Host Fees") and/or Guests ("Guest Fees")
        (collectively, "Service Fees") in consideration for the use of the hostU Platform. A
        transaction will be deemed to be originated on the hostU Platform and the hostU Fee due and
        payable if (i) a booking is completed on the hostU Platform, (ii) a Guest finds a Listing on
        the hostU Platform and completes a booking of the Listing off the hostU Platform, or (iii)
        Members are introduced to each other on the hostU Platform and subsequently transact over
        one or more Listings. The hostU Fee is due and payable regardless of whether the Guest makes
        full or partial payments under the terms of the Sublet Agreement or otherwise.
      </p>
      <p>
        6.2 hostU reserves the right to change the Service Fees at any time and will provide Members
        adequate notice of any fee changes before they become effective. Such fee changes will not
        affect any bookings made prior to the effective date of the fee change, but will affect any
        Listings that have not yet completed bookings. In the event of any change in Service Fees, a
        Host can remove a booking at any time.
      </p>
      <p>
        6.3 You are responsible for paying any Service Fees that you owe to hostU. The applicable
        Service Fees (including any applicable Taxes) are collected by hostU. hostU will deduct any
        Service Fees before remitting the payout to the Host. Any Guest Fees are included in the
        Total Fees collected by hostU. Except as otherwise provided on the hostU Platform, Service
        Fees are non-refundable.
      </p>
      <p className="font-semibold text-3xl">7. Terms specific for Hosts</p>
      <p>7.1 Terms applicable to all Listings</p>
      <p>
        7.1.1 When creating a Listing through the hostU Platform you must (i) provide complete and
        accurate information about your Listing (such as University affiliation, listing
        description, location, and calendar availability), (ii) disclose any deficiencies,
        restrictions (such as house rules) and requirements that apply and (iii) provide any other
        pertinent information requested by hostU. You are responsible for keeping your Listing
        information (including calendar availability) up to date at all times.
      </p>
      <p>
        7.1.2 You are solely responsible for setting a price (including any Taxes if applicable, or
        charges such as cleaning fees) for your Listing (“Listing Fee”). Once a Guest requests a
        booking of your Listing, you may not request that the Guest pays a higher price than in the
        booking request.
      </p>
      <p>
        7.1.3 Any terms and conditions included in your Listing, in particular in relation to
        cancellations, must not conflict with these Terms or the relevant cancellation policy for
        your Listing.
      </p>
      <p>
        7.1.4 Pictures, animations or videos (collectively, "Images") used in your Listings must
        accurately reflect the quality and condition of your Listing. hostU reserves the right to
        require that Listings have a minimum number of Images of a certain format, size and
        resolution.
      </p>
      <p>
        7.1.5 The placement and ranking of Listings in search results on the hostU Platform may vary
        and depend on a variety of factors, such as Guest search parameters and preferences, Host
        requirements, price and calendar availability, number and quality of Images, customer
        service and cancellation history, Reviews and Ratings, type of Host Service, and/or ease of
        booking. More information about the factors that determine how your Listing appears in
        search results can be found on our help center.
      </p>
      <p>
        7.1.7 When you accept or have pre-approved a booking request by a Guest, you are entering
        into a legally binding agreement with the Guest and are required to provide access and use
        of the accommodation as described in your Listing when the booking request is made upon the
        terms set forth in the Sublet Agreement and these Terms of Service. You also agree to pay
        the applicable hostU Fee and any applicable Taxes.
      </p>
      <p>
        7.1.8 hostU recommends that Hosts obtain appropriate insurance for their Listing. Please
        review any respective insurance policy carefully, and in particular make sure that you are
        familiar with and understand any exclusions to, and any deductibles that may apply for, such
        insurance policy, including, but not limited to, whether or not your insurance policy will
        cover the actions or inactions of Guests (and the individuals the Guest has booked for, if
        applicable) while staying at your Accommodation or participating in your Experience, Event
        or other Host Service.
      </p>
      <p>7.2 Listing Accommodations</p>
      <p>
        7.2.1 Unless expressly allowed by hostU, you may not list more than one Accommodation per
        Listing.
      </p>
      <p>
        7.2.2 You represent and warrant that any Listing you post and the booking of, or a Guest's
        stay at, an Accommodation will (i) not breach any agreements you have entered into with any
        third parties, such as your rental agreement, homeowners association, condominium, or other
        agreements, and (ii) comply with all applicable laws (such as zoning laws), Tax
        requirements, and other rules and regulations (including having all required permits,
        licenses and registrations). As a Host, you are responsible for your own acts and omissions
        and are also responsible for the acts and omissions of any individuals who reside at or are
        otherwise present at the Accommodation at your request or invitation, excluding the Guest
        and any individuals the Guest invites to the Accommodation.
      </p>
      <p className="font-semibold text-3xl">8. Terms specific for Guests</p>
      <p>8.1 Terms applicable to all bookings</p>
      <p>
        8.1.1 Subject to meeting any requirements (such as completing any verification processes)
        set by hostU and/or the Host, you can book a Listing available on the hostU Platform by
        following the respective booking process. All applicable fees, including the hostU Fee,
        Security Deposit (if applicable), and any applicable Taxes (collectively, “Total Fees”) will
        be presented to you prior to booking a Listing. You agree to pay the Total Fees for any
        booking requested in connection with your hostU Account.
      </p>
      <p>
        8.1.2 Upon receipt of a booking confirmation from hostU, a legally binding agreement is
        formed between you and your Host that incorporate these Terms of Service and the Sublet
        Agreement, subject to any additional terms and conditions of the Host that apply, including
        any rules and restrictions specified in the Listing. For certain bookings, Guests may be
        required to pay or have the option to pay in multiple installments.
      </p>
      <p>
        8.1.3 If you are booking for an additional guest who is a minor, you represent and warrant
        that you are legally authorized to act on behalf of the minor. Minors may only stay in a
        booking if agreed upon by Host and accompanied by an adult who is responsible for them.
      </p>
      <p>8.2 Booking Accommodations</p>
      <p>
        8.2.1 You understand that a confirmed booking of an Accommodation (“Accommodation Booking”)
        is a limited license granted to you by the Host and the Sublease Agreement to enter, occupy
        and use the Accommodation for the duration of your stay.
      </p>
      <p>
        8.2.2 You agree to leave the Accommodation no later than the checkout time that the Host
        specifies in the Listing, or such other time as mutually agreed upon between you and the
        Host. If you stay past the agreed upon checkout time without the Host's consent
        (“Overstay”), you no longer have a right to stay in the Accommodation and the Host is
        entitled to make you leave in a manner consistent with applicable law. In addition, you
        agree to pay, if requested by the Host, for each twenty-four (24) hour period (or any
        portion thereof) that you Overstay, an additional nightly fee of up to two (2) times the
        average nightly Listing Fee originally paid by you to cover the inconvenience suffered by
        the Host, plus all applicable hostU, Taxes, and any legal expenses incurred by the Host to
        make you leave (collectively, "Overstay Fees"). If you Overstay at an Accommodation, you
        authorize hostU to charge you to collect Overstay Fees. A Security Deposit, if required by a
        Host, may be applied to any Overstay Fees due for a Guest’s Overstay.
      </p>
      <p className="font-semibold text-3xl">9. Booking Modifications</p>
      <p>
        9.1 Hosts and Guests are responsible for any modifications to a booking that they make via
        the hostU Platform or direct hostU customer service to make ("Booking Modifications"), and
        agree to pay any additional hostU Fees and/or Taxes associated with such Booking
        Modifications.
      </p>
      <p>
        9.2 Bookings are not cancellable or refundable, unless the Guest and the Host mutually agree
        in writing to cancel a booking. Upon receiving an executed written agreement, hostU will
        return any Listing Fee still held by hostU less the hostU Fee.
      </p>
      <p className="font-semibold text-3xl">10. hostU Cancellations</p>
      <p>
        10.1 In certain circumstances, hostU may decide, in its sole discretion, that it is
        necessary to cancel a pending or confirmed booking and initiate corresponding refunds and
        payouts. This may be (i) where hostU believes in good faith, while taking the legitimate
        interests of both parties into account, this is necessary to avoid significant harm to
        hostU, other Members, third parties or property, or (ii) for any of the reasons set out in
        these Terms.
      </p>
      <p className="font-semibold text-3xl">
        11. Damage to Accommodations, Disputes between Members
      </p>
      <p>
        11.1 As a Guest, you are responsible for leaving the Accommodation (including any personal
        or other property located at the Accommodation) in the condition it was in when you arrived.
        You are responsible for your own acts and omissions and are also responsible for the acts
        and omissions of any individuals whom you invite to, or otherwise provide access to, the
        Accommodation, excluding the Host (and the individuals the Host invites to the
        Accommodation, if applicable).
      </p>
      <p>
        11.2 Guests and Hosts agree to cooperate with each other, and to provide the other party
        with such information and take such actions as may be reasonably requested by hostU, in
        connection with any Damage Claims or other complaints or claims made by Members relating to
        Accommodations or any personal or other property located at an Accommodation. A Member
        shall, upon the other party’s reasonable request, participate in mediation or a similar
        resolution process with another Member, with respect to losses for which a Member is
        requesting payment from the other party, and the Members agree to split the expenses of such
        mediation.
      </p>
      <p className="font-semibold text-3xl">12. Rounding off</p>
      <p>
        hostU generally supports payment amounts that are payable from or to Guests or Hosts to the
        smallest unit supported by a given currency (i.e., U.S. cents, Euro cents or other supported
        currencies). Where hostU’s third-party payment services provider does not support payments
        in the smaller unit supported by a given currency, hostU may, in its sole discretion, round
        up or round down the displayed amounts that are payable from or to Guests or Hosts to the
        nearest whole functional base unit in which the currency is denominated (i.e. to the nearest
        dollar, Euro or other supported currency); for example, hostU may round up an amount of
        $101.50 to $102.00, and round down an amount of $101.49 to $101.00.
      </p>
      <p className="font-semibold text-3xl">13. Taxes</p>
      <p>
        13.1 As a Host you are solely responsible for determining your obligations to report,
        collect, remit or include in your Listing Fees any applicable VAT or other indirect sales
        taxes, occupancy tax, tourist or other visitor taxes or income taxes ("Taxes").
      </p>
      <p className="font-semibold text-3xl">14. Prohibited Activities</p>
      <p>
        14.1 You are solely responsible for compliance with any and all laws, rules, regulations,
        and Tax obligations that may apply to your use of the hostU Platform. In connection with
        your use of the hostU Platform, you will not and will not assist or enable others to:
      </p>
      <ul className="list-disc pl-6" itemType="disc">
        <li>
          breach or circumvent any applicable laws or regulations, agreements with third-parties,
          third-party rights, or our Terms, Policies or Standards;
        </li>
        <li>
          use the hostU Platform or Collective Content for any commercial or other purposes that are
          not expressly permitted by these Terms or in a manner that falsely implies hostU
          endorsement, partnership or otherwise misleads others as to your affiliation with hostU;
        </li>
        <li>
          copy, store, or otherwise access or use any information, including personally identifiable
          information about any other Member, contained on the hostU Platform in any way that is
          inconsistent with hostU’s Privacy Policy or these Terms or that otherwise violates the
          privacy rights of Members or third parties;
        </li>
        <li>
          use the hostU Platform in connection with the distribution of unsolicited commercial
          messages ("spam");
        </li>
        <li>
          offer, as a Host, any Accommodation that you do not yourself own or have permission to
          make available as a residential or other property through the hostU Platform;
        </li>
        <li>
          unless hostU explicitly permits otherwise, book any Listing if you will not actually be
          using the Listing yourself;
        </li>
        <li>
          contact another Member for any purpose other than asking a question related to your own
          booking, Listing, or the Member's use of the hostU Platform, including, but not limited
          to, recruiting or otherwise soliciting any Member to join third-party services,
          applications or websites, without our prior written approval;
        </li>
        <li>
          take any transaction off the hostU Platform and/or use the hostU Platform to request, make
          or accept a sublease or booking independent of the hostU Platform, to circumvent any hostU
          Fees or for any other reason;
        </li>
        <li>
          request, accept or make any payment for Listing Fees outside of the hostU Platform or
          hostU Payments. If you do so, you acknowledge and agree that you: (i) would be in breach
          of these Terms; (ii) accept all risks and responsibility for such payment, and (iii) hold
          hostU harmless from any liability for such payment;
        </li>
        <li>
          discriminate against or harass anyone on the basis of race, national origin, religion,
          gender, gender identity, physical or mental disability, medical condition, marital status,
          age or sexual orientation, or otherwise engage in any violent, harmful, abusive or
          disruptive behavior;
        </li>
        <li>
          misuse or abuse any Listings or services associated with the hostU Open Homes program as
          determined by hostU in its sole discretion;
        </li>
        <li>
          use, display, mirror or frame the hostU Platform or Collective Content, or any individual
          element within the hostU Platform, hostU's name, any hostU trademark, logo or other
          proprietary information, or the layout and design of any page or form contained on a page
          in the hostU Platform, without hostU's express written consent;
        </li>
        <li>
          dilute, tarnish or otherwise harm the hostU brand in any way, including through
          unauthorized use of Collective Content, registering and/or using hostU or derivative terms
          in domain names, trade names, trademarks or other source identifiers, or registering
          and/or using domains names, trade names, trademarks or other source identifiers that
          closely imitate or are confusingly similar to hostU domains, trademarks, taglines,
          promotional campaigns or Collective Content;
        </li>
        <li>
          use any robots, spider, crawler, scraper or other automated means or processes to access,
          collect data or other content from or otherwise interact with the hostU Platform for any
          purpose;
        </li>
        <li>
          avoid, bypass, remove, deactivate, impair, descramble, or otherwise attempt to circumvent
          any technological measure implemented by hostU or any of hostU's providers or any other
          third party to protect the hostU Platform;
        </li>
        <li>
          attempt to decipher, decompile, disassemble or reverse engineer any of the software used
          to provide the hostU Platform;
        </li>
        <li>
          take any action that damages or adversely affects, or could damage or adversely affect the
          performance or proper functioning of the hostU Platform;
        </li>
        <li>
          export, re-export, import, or transfer the Application except as authorized by United
          States law, the export control laws of your jurisdiction, and any other applicable laws;
          or
        </li>
        <li>violate or infringe anyone else’s rights or otherwise cause harm to anyone.</li>
      </ul>
      <p>
        14.2 You acknowledge that hostU has no obligation to monitor the access to or use of the
        hostU Platform by any Member or to review, disable access to, or edit any Member Content,
        but has the right to do so to (i) operate, secure and improve the hostU Platform (including
        without limitation for fraud prevention, risk assessment, investigation and customer support
        purposes); (ii) ensure Members’ compliance with these Terms; (iii) comply with applicable
        law or the order or requirement of a court, law enforcement or other administrative agency
        or governmental body; (iv) collect the hostU Fee for any transaction that Members complete
        off the hostU Platform along with any collection costs of doing so, including attorney’s
        costs and fees; (v) respond to Member Content that it determines is harmful or
        objectionable; or (vi) as otherwise set forth in these Terms. Members agree to cooperate
        with and assist hostU in good faith, and to provide hostU with such information and take
        such actions as may be reasonably requested by hostU with respect to any investigation
        undertaken by hostU or a representative of hostU regarding the use or abuse of the hostU
        Platform.
      </p>
      <p>
        14.3 If you feel that any Member you interact with, whether online or in person, is acting
        or has acted inappropriately, including but not limited to anyone who (i) engages in
        offensive, violent or sexually inappropriate behavior, (ii) you suspect of stealing from
        you, or (iii) engages in any other disturbing conduct, you should immediately report such
        person to the appropriate authorities and then to hostU by contacting us with your police
        station and report number (if available). You agree that any report you make will not
        obligate us to take any action (beyond that required by law, if any).
      </p>
      <p className="font-semibold text-3xl">
        15. Term and Termination, Suspension and other Measures
      </p>
      <p>
        15.1 This Agreement shall be effective for a 30-day term, at the end of which it will
        automatically and continuously renew for subsequent 30-day terms until such time when you or
        hostU terminate the Agreement in accordance with this provision.
      </p>
      <p>
        15.2 You may terminate this Agreement at any time that you have a Listing by sending us an
        email which has the effect of cancelling your hostU Account, subject to those terms and
        conditions which survive termination of this Agreement, including without limitation, your
        obligation to pay hostU Fees and reasonable attorneys fees for hostU’s collection efforts.
        If you are a Guest or Host with an active booking, you may only cancel this Agreement after
        conclusion of the booking and any dispute related to the booking.
      </p>
      <p>
        15.3 Without limiting our rights specified below, hostU may terminate this Agreement for
        convenience at any time by giving you thirty (30) days' notice via email to your registered
        email address.
      </p>
      <p>
        15.4 hostU may immediately, without notice, terminate this Agreement and/or stop providing
        access to the hostU Platform if (i) you have materially breached your obligations under
        these Terms, the Payments Terms, our Policies or Standards, (ii) you have violated
        applicable laws, regulations or third party rights, or (iii) hostU believes in good faith
        that such action is reasonably necessary to protect the personal safety or property of
        hostU, its Members, or third parties (for example in the case of fraudulent behavior of a
        Member).
      </p>
      <p>
        15.5 In addition, hostU may take any of the following measures (i) to comply with applicable
        law, or the order or request of a court, law enforcement or other administrative agency or
        governmental body, or if (ii) you have breached these Terms, the Payments Terms, our
        Policies or Standards, applicable laws, regulations, or third party rights, (iii) you have
        provided inaccurate, fraudulent, outdated or incomplete information during the hostU Account
        registration, Listing process or thereafter, (iv) you and/or your Listings at any time fail
        to meet any applicable quality or eligibility criteria, (v) hostU otherwise becomes aware of
        or has received complaints about your performance or conduct, (vi) you have repeatedly
        cancelled confirmed bookings or failed to respond to booking requests without a valid
        reason, or (vii) hostU believes in good faith that such action is reasonably necessary to
        protect the personal safety or property of hostU, its Members, or third parties, or to
        prevent fraud or other illegal activity:
      </p>
      <ul className="list-disc pl-6" itemType="disc">
        <li>
          refuse to surface, delete or delay any Listings, Ratings, Reviews, or other Member
          Content;
        </li>
        <li>cancel any pending or confirmed bookings;</li>
        <li>limit your access to or use of the hostU Platform;</li>
        <li>
          temporarily or permanently revoke any special status associated with your hostU Account;
        </li>
        <li>
          temporarily or in case of severe or repeated offenses permanently suspend your hostU
          Account and stop providing access to the hostU Platform.
        </li>
        <li>
          In case of non-material breaches and where appropriate, you will be given notice of any
          intended measure by hostU and an opportunity to resolve the issue to hostU's reasonable
          satisfaction.
        </li>
      </ul>

      <p>
        15.6 If we take any of the measures described above (i) we may refund your Guests in full
        for any and all confirmed bookings that have been cancelled, irrespective of preexisting
        cancellation policies, and (ii) you will not be entitled to any compensation for pending or
        confirmed bookings that were cancelled.
      </p>
      <p>
        15.7 When this Agreement has been terminated, you are not entitled to a restoration of your
        hostU Account or any of your Member Content. If your access to or use of the hostU Platform
        has been limited or your hostU Account has been suspended or this Agreement has been
        terminated by us, you may not register a new hostU Account or access and use the hostU
        Platform through an hostU Account of another Member.
      </p>
      <p>
        15.8 Sections 5 through 9 and 11 through through 22 of these Terms shall survive any
        termination or expiration of this Agreement.
      </p>
      <p className="font-semibold text-3xl">16. Disclaimers</p>
      <p>
        If you choose to use the hostU Platform or Collective Content, you do so voluntarily and at
        your sole risk. The hostU Platform and Collective Content is provided “as is”, without
        warranty of any kind, either express or implied.
      </p>
      <p>
        You agree that you have had whatever opportunity you deem necessary to investigate the hostU
        Services, laws, rules, or regulations that may be applicable to your Listings and/or Listing
        you are receiving and that you are not relying upon any statement of law or fact made by
        hostU relating to a Listing.
      </p>
      <p>
        If we choose to conduct identity verification or background checks on any Member, to the
        extent permitted by applicable law, we disclaim warranties of any kind, either express or
        implied, that such checks will identify prior misconduct by a Member or guarantee that a
        Member will not engage in misconduct in the future.
      </p>
      <p>
        The foregoing disclaimers apply to the maximum extent permitted by law. You may have other
        statutory rights. However, the duration of statutorily required warranties, if any, shall be
        limited to the maximum extent permitted by law.
      </p>
      <p className="font-semibold text-3xl">17. Liability</p>
      <p>
        You acknowledge and agree that, to the maximum extent permitted by law, the entire risk
        arising out of your access to and use of the hostU Platform and Collective Content, your
        publishing or booking of any Listing via the hostU Platform, your stay at any Accommodation,
        or any other interaction you have with other Members whether in person or online remains
        with you. Neither hostU nor any other party involved in creating, producing, or delivering
        the hostU Platform or Collective Content will be liable for any incidental, special,
        exemplary or consequential damages, including lost profits, loss of data or loss of
        goodwill, service interruption, computer damage or system failure or the cost of substitute
        products or services, or for any damages for personal or bodily injury or emotional distress
        arising out of or in connection with (i) these Terms, (ii) from the use of or inability to
        use the hostU Platform or Collective Content, (iii) from any communications, interactions or
        meetings with other Members or other persons with whom you communicate, interact or meet
        with as a result of your use of the hostU Platform, or (iv) from your publishing or booking
        of a Listing, including the provision or use of a Listing’s Listing, whether based on
        warranty, contract, tort (including negligence), product liability or any other legal
        theory, and whether or not hostU has been informed of the possibility of such damage, even
        if a limited remedy set forth herein is found to have failed of its essential purpose.
        Except for our obligations to pay amounts to applicable Hosts and hostU pursuant to these
        Terms or an approved payment request, in no event will hostU’s aggregate liability arising
        out of or in connection with these Terms and your use of the hostU Platform including, but
        not limited to, from your publishing or booking of any Listings via the hostU Platform, or
        from the use of or inability to use the hostU Platform or Collective Content and in
        connection with any Accommodation, or interactions with any other Members, exceed the
        amounts you have paid or owe for bookings via the hostU Platform as a Guest in the twelve
        (12) month period prior to the event giving rise to the liability, or if you are a Host, the
        amounts paid by hostU to you in the twelve (12) month period prior to the event giving rise
        to the liability, or one hundred U.S. dollars (US$100), if no such payments have been made,
        as applicable. The limitations of damages set forth above are fundamental elements of the
        basis of the bargain between hostU and you. Some jurisdictions do not allow the exclusion or
        limitation of liability for consequential or incidental damages, so the above limitation may
        not apply to you. If you reside outside of the U.S., this does not affect hostU’s liability
        for any liability which cannot be excluded or limited under applicable law.
      </p>
      <p className="font-semibold text-3xl">18. Indemnification</p>
      <p>
        To the maximum extent permitted by applicable law, you agree to release, defend (at hostU’s
        option), indemnify, and hold hostU and its affiliates and subsidiaries, including but not
        limited to, hostU Payments, and their shareholders, officers, directors, employees and
        agents, harmless from and against any claims, liabilities, damages, losses, and expenses,
        including, without limitation, legal and accounting fees, arising out of or in any way
        connected with (i) your breach of these Terms or our Policies or Standards, (ii) your
        improper use of the hostU Platform or any hostU Services, (iii) your interaction with any
        Member, stay at an Accommodation, including without limitation any injuries, losses or
        damages (whether compensatory, direct, incidental, consequential or otherwise) of any kind
        arising in connection with or as a result of such interaction, stay, participation or use,
        (iv) hostU’s Collection and Remittance of Occupancy Taxes, or (v) your breach of any laws,
        regulations or third party rights.
      </p>
      <p className="font-semibold text-3xl">19. Dispute Resolution and Arbitration Agreement</p>
      <p>
        19.1 This Dispute Resolution and Arbitration Agreement shall apply if your (i) country of
        residence or establishment is in the United States; or (ii) your country of residence or
        establishment is not in the United States but bring any claim against hostU in the United
        States (to the extent not in conflict with Section 21).
      </p>
      <p>
        19.2 Overview of Dispute Resolution Process. hostU is committed to participating in a
        consumer-friendly dispute resolution process. To that end, these Terms provide for a
        two-part process for individuals to whom Section 19.1 applies: (1) an informal mediation
        between the Host and Guest, and (2) a binding arbitration administered by the American
        Arbitration Association (“AAA”) using its specially designed Consumer Arbitration Rules (as
        modified by this Section 19 and except as provided in Section 19.6). Specifically, the
        Consumer Arbitration Rules provide:
      </p>
      <ul className="list-disc pl-6" itemType="disc">
        <li>Claims can be filed with AAA online (www.adr.org);</li>
        <li>Arbitrators must be neutral, and no party may unilaterally select an arbitrator;</li>
        <li>
          Arbitrators must disclose any bias, interest in the result of the arbitration, or
          relationship with any party;
        </li>
        <li>The initial filing fee for the consumer is capped at $200;</li>
        <li>
          The consumer gets to elect the hearing location and can elect to participate live, by
          phone, video conference, or, for claims under $5,000, by the submission of documents;
        </li>
        <li>
          The arbitrator can grant any remedy that the parties could have received in court to
          resolve the party’s individual claim.
        </li>
      </ul>
      <p>
        19.3 Pre-Arbitration Dispute Resolution and Notification. Prior to initiating an
        arbitration, you and hostU each agree to notify the other party of the dispute and attempt
        to negotiate an informal resolution to it first. If after a good faith effort to negotiate
        one of us feels the dispute has not and cannot be resolved informally, the party intending
        to pursue arbitration agrees to notify the other party via email prior to initiating the
        arbitration. In order to initiate arbitration, a claim must be filed with the AAA and the
        written Demand for Arbitration (available at www.adr.org) provided to the other party, as
        specified in the AAA Rules.
      </p>
      <p>
        19.4 Agreement to Arbitrate. You and hostU mutually agree that any dispute, claim or
        controversy arising out of or relating to these Terms or the applicability, breach,
        termination, validity, enforcement or interpretation thereof, or to the use of the hostU
        Platform, the Listing, or the Collective Content (collectively, “Disputes”) will be settled
        by binding individual arbitration (the “Arbitration Agreement”). If there is a dispute about
        whether this Arbitration Agreement can be enforced or applies to our Dispute, you and hostU
        agree that the arbitrator will decide that issue.
      </p>
      <p>
        19.5 Exceptions to Arbitration Agreement. You and hostU each agree that the following claims
        are exceptions to the Arbitration Agreement and will be brought in a judicial proceeding in
        a court of competent jurisdiction: (i) Any claim related to actual or threatened
        infringement, misappropriation or violation of a party’s copyrights, trademarks, trade
        secrets, patents, or other intellectual property rights; (ii) Any claim seeking emergency
        injunctive relief based on exigent circumstances (e.g., imminent danger or commission of a
        crime, hacking, cyber-attack).
      </p>
      <p>
        19.6 Arbitration Rules and Governing Law. This Arbitration Agreement evidences a transaction
        in interstate commerce and thus the Federal Arbitration Act governs the interpretation and
        enforcement of this provision. The arbitration will be administered by AAA in accordance
        with the Consumer Arbitration Rules and/or other AAA arbitration rules determined to be
        applicable by the AAA (the “AAA Rules“) then in effect, except as modified here. The AAA
        Rules are available at www.adr.org or by calling the AAA at 1–800–778–7879.
      </p>
      <p>
        19.7 Modification to AAA Rules - Arbitration Hearing/Location. You and hostU agrees that any
        required arbitration hearing may be conducted, at your option, (a) in Chicago, Illinois; (b)
        in any other location to which you and hostU both agree; (c) via phone or video conference;
        or (d) for any claim or counterclaim under $5,000, by solely the submission of documents to
        the arbitrator.
      </p>
      <p>
        19.8 Arbitrator’s Decision. The arbitrator’s decision will include the essential findings
        and conclusions upon which the arbitrator based the award. Judgment on the arbitration award
        may be entered in any court with proper jurisdiction. The arbitrator may award declaratory
        or injunctive relief only on an individual basis and only to the extent necessary to provide
        relief warranted by the claimant’s individual claim.
      </p>
      <p>
        19.9 Jury Trial Waiver. You and hostU acknowledge and agree that we are each waiving the
        right to a trial by jury as to all arbitrable Disputes.
      </p>
      <p>
        19.10 No Class Actions or Representative Proceedings. You and hostU acknowledge and agree
        that, to the fullest extent permitted by law, we are each waiving the right to participate
        as a plaintiff or class member in any purported class action lawsuit, class-wide
        arbitration, private attorney general action, or any other representative proceeding as to
        all Disputes. Further, unless you and hostU both otherwise agree in writing, the arbitrator
        may not consolidate more than one party’s claims and may not otherwise preside over any form
        of any class or representative proceeding. If the “class action lawsuit” waiver or the
        “class-wide arbitration” waiver in this Section 19.10 is held unenforceable with respect to
        any Dispute, then the entirety of the Arbitration Agreement will be deemed void with respect
        to such Dispute and the Dispute must proceed in court. If the “private attorney general
        action” waiver or the “representative proceeding” waiver in this Section 19.10 is held
        unenforceable with respect to any Dispute, those waivers may be severed from this
        Arbitration Agreement and you and hostU agree that any private attorney general claims and
        representative claims in the Dispute will be severed and stayed, pending the resolution of
        any arbitrable claims in the Dispute in individual arbitration.
      </p>
      <p>
        19.11 Severability. Except as provided in Section 19.10, in the event that any portion of
        this Arbitration Agreement is deemed illegal or unenforceable, such provision shall be
        severed and the remainder of the Arbitration Agreement shall be given full force and effect.
      </p>
      <p>
        19.12 Changes. Notwithstanding the provisions of Section 3 (“Modification of these Terms”),
        if hostU changes this Section 19 (“Dispute Resolution and Arbitration Agreement”) after the
        date you last accepted these Terms (or accepted any subsequent changes to these Terms), you
        may reject any such change by sending us written notice (including by email) within thirty
        (30) days of the date such change became effective, as indicated in the “Last Updated” date
        above or in the date of hostU’s email to you notifying you of such change. Rejecting a new
        change, however, does not revoke or alter your prior consent to any earlier agreements to
        arbitrate any Dispute between you and hostU (or your prior consent to any subsequent changes
        thereto), which will remain in effect and enforceable as to any Dispute between you and
        hostU.
      </p>
      <p>
        19.13 Survival. Except as provided in Section 19.11 and subject to Section 15.8, this
        Section 19 will survive any termination of these Terms and will continue to apply even if
        you stop using the hostU Platform or terminate your hostU Account.
      </p>
      <p className="font-semibold text-3xl">20. Feedback</p>
      <p>
        We welcome and encourage you to provide feedback, comments and suggestions for improvements
        to the hostU Platform (“Feedback“). You may submit Feedback by emailing us, through the
        “Contact” section of the hostU Platform, or by other means of communication. Any Feedback
        you submit to us will be considered non-confidential and non-proprietary to you. By
        submitting Feedback to us, you grant us a non-exclusive, worldwide, royalty-free,
        irrevocable, sub-licensable, perpetual license to use and publish those ideas and materials
        for any purpose, without compensation to you.
      </p>
      <p className="font-semibold text-3xl">21. Applicable Law and Jurisdiction</p>
      <p>
        21.1 These Terms will be interpreted in accordance with the laws of the State of Illinois
        and the United States of America, without regard to conflict-of-law provisions. Judicial
        proceedings (other than small claims actions) that are excluded from the Arbitration
        Agreement in Section 19 must be brought in state or federal court in Chicago, Illinois,
        unless we both agree to some other location. You and we both consent to venue and personal
        jurisdiction in Chicago, Illinois.
      </p>
      <p className="font-semibold text-3xl">22. General Provisions</p>
      <p>
        22.1 Except as they may be supplemented by additional terms and conditions, policies,
        guidelines or standards, these Terms constitute the entire Agreement between hostU and you
        pertaining to the subject matter hereof and supersede any and all prior oral or written
        understandings or agreements between hostU and you in relation to the access to and use of
        the hostU Platform.
      </p>
      <p>
        22.2 No joint venture, partnership, employment, or agency relationship exists between you
        and hostU as a result of this Agreement or your use of the hostU Platform.
      </p>
      <p>
        22.3 These Terms do not and are not intended to confer any rights or remedies upon any
        person other than the parties.
      </p>
      <p>
        22.4 If any provision in these Terms is determined to be illegal or unenforceable, that
        provision is severed from this Agreement and the other provisions remain in force.
      </p>
      <p>
        22.5 hostU’s failure to enforce any right or provision in these Terms will not constitute a
        waiver of such right or provision unless acknowledged and agreed to by us in writing. Except
        as expressly set forth in these Terms, the exercise by either party of any of its remedies
        under these Terms will be without prejudice to its other remedies under these Terms or
        otherwise permitted under law.
      </p>
      <p>
        22.6 You may not assign, transfer or delegate this Agreement and your rights and obligations
        hereunder without hostU's prior written consent. hostU may without restriction assign,
        transfer or delegate this Agreement and any rights and obligations hereunder, at its sole
        discretion, with 30 days prior notice. Your right to terminate this Agreement at any time
        remains unaffected.
      </p>
      <p>
        22.7 Unless specified otherwise, any notices or other communications to Members permitted or
        required under this Agreement, will be provided electronically and given by hostU via email,
        hostU Platform notification, or messaging service.
      </p>
    </div>
  </div>
));

export default termsOfService;
